import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/query/react'
import { isEmpty, isEqual, omit } from 'lodash-es'
import { WorkspacesAPITagType, api } from '@/api/api'
import getCollaborationRecordScoringInvalidatesTags from '@/api/utils/get-collaboration-record-scoring-invalidates-tags'
import getWorkspaceApiUrl from '@/api/utils/get-workspace-api-url'
import defaultStringifyRequest from '@/constants/default-stringify-request'
import {
  addCollaborationRecordScoringResponseSchema,
  addCustomizedCollaborationRecordResponseSchema,
  fetchCollaborationRecordsResponseSchema,
  fetchCustomPostCollaborationRecordsResponseSchema,
  type AddCollaborationRecordScoringRequest,
  type AddCollaborationRecordScoringResponse,
  type AddCustomizedCollaborationRecordRequest,
  type AddCustomizedCollaborationRecordResponse,
  type DeleteCollaborationRecordScoringRequest,
  type DeleteCustomizedCollaborationRecordRequest,
  type EditCollaborationRecordScoringRequest,
  type FetchCollaborationRecordsResponse,
  type FetchCustomPostCollaborationRecordsResponse,
  type UpdateCustomizedCollaborationRecordRequest,
} from '@/types/schema/directory/collaboration-record'
import type { FetchKolCollectionSearchFieldsRequest } from '@/types/schema/directory/kol-collection'
import {
  AddKolContactInfoRequest,
  AddNoteRequest,
  DeleteKolContactInfoRequest,
  DeleteNoteRequest,
  DirectoryCustomizedSearchLeafField,
  DirectorySearchFields,
  DirectorySearchFieldsResponse,
  EditKolBasicInfoRequest,
  EditKolContactInfoRequest,
  FetchCustomizedFieldsRequest,
  FetchCustomizedFieldsResponse,
  FetchCustomizedLabelsResponse,
  FetchFilterResourcesInfiniteResponse,
  FetchFilterResourcesRequest,
  FetchKolBasicInfoRequest,
  FetchKolBasicInfoResponse,
  FetchKolContactInfoRequest,
  FetchKolContactInfoResponse,
  FetchKolFootprintRequest,
  FetchKolFootprintResponse,
  FetchKolManagementRequest,
  FetchKolManagementResponse,
  FetchNoteRequest,
  FetchNoteResponse,
  KolCollectionBasicRequest,
  Note,
  UpdateCustomizedFieldValueRequest,
  addKolsToDirectoryResponseSchema,
  directoryCustomizedSearchLeafFieldSchema,
  directorySearchFieldsResponseSchema,
  fetchCustomizedFieldsSchema,
  fetchCustomizedLabelsResponseSchema,
  fetchFilterResourcesInfiniteSchema,
  fetchKolBasicInfoResponseSchema,
  fetchKolContactInfoResponseSchema,
  fetchKolFootprintResponseSchema,
  fetchKolManagementCollaborationRecordsResponseSchema,
  fetchKolManagementResponseSchema,
  fetchNoteResponseSchema,
  kolManagementStatusSchema,
  type AddKolsToDirectoryRequest,
  type AddKolsToDirectoryResponse,
  type ChangeKolCollectionCustomizedFieldRequest,
  type CreateKolCollectionCustomizedFieldRequest,
  type DeleteKolBasicInfoRequest,
  type DeleteKolCollectionCustomizedFieldRequest,
  type FetchKolManagementCollaborationRecordsRequest,
  type FetchKolManagementCollaborationRecordsResponse,
  type KolManagementStatus,
} from '@/types/schema/directory/kol-management'
import removeNil from '@/utils/remove-nil'

const directoryApi = api.injectEndpoints({
  endpoints: (builder) => ({
    fetchKolManagement: builder.query<
      FetchKolManagementResponse,
      FetchKolManagementRequest
    >({
      query: (params) => {
        return {
          url: getWorkspaceApiUrl('/directory/kol-management', 'v3'),
          params: removeNil({ ...params, ...defaultStringifyRequest }),
        }
      },
      extraOptions: {
        dataSchema: fetchKolManagementResponseSchema,
      },
      providesTags: [
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.DirectoryKolCollectionInfo,
      ],
    }),
    addKolsToDirectory: builder.mutation<
      AddKolsToDirectoryResponse,
      AddKolsToDirectoryRequest
    >({
      query: (body) => {
        return {
          url: getWorkspaceApiUrl('/directory/kol-management', 'v3'),
          method: 'POST',
          body,
        }
      },
      extraOptions: {
        dataSchema: addKolsToDirectoryResponseSchema,
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolManagementStatus,
      ],
    }),
    removeKolsFromDirectory: builder.mutation<void, string[]>({
      query: (ids) => {
        return {
          url: getWorkspaceApiUrl('/directory/kol-management', 'v3'),
          method: 'DELETE',
          body: ids,
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolInfo,
        WorkspacesAPITagType.KolManagement,
      ],
    }),
    addCollaborationRecordScoring: builder.mutation<
      AddCollaborationRecordScoringResponse,
      AddCollaborationRecordScoringRequest
    >({
      query: ({ kolId, ...body }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/directory/collaboration-record-scoring`,
          'v3',
        ),
        method: 'POST',
        body,
      }),
      extraOptions: {
        dataSchema: addCollaborationRecordScoringResponseSchema,
      },
      invalidatesTags: (_result, _error, arg) => {
        return getCollaborationRecordScoringInvalidatesTags(
          arg.collaboration_record_scoring_type,
        )
      },
    }),
    editCollaborationRecordScoring: builder.mutation<
      void,
      EditCollaborationRecordScoringRequest
    >({
      query: ({ kolId, collaborationRecordScoringId, ...body }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/directory/collaboration-record-scorings/${collaborationRecordScoringId}`,
          'v3',
        ),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => {
        return getCollaborationRecordScoringInvalidatesTags(
          arg.collaboration_record_scoring_type,
        )
      },
    }),
    deleteCollaborationRecordScoring: builder.mutation<
      void,
      DeleteCollaborationRecordScoringRequest
    >({
      query: ({ kolId, collaborationRecordScoringId }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/directory/collaboration-record-scorings/${collaborationRecordScoringId}`,
          'v3',
        ),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => {
        return getCollaborationRecordScoringInvalidatesTags(
          arg.collaborationRecordScoringType,
        )
      },
    }),
    fetchNote: builder.query<FetchNoteResponse, FetchNoteRequest>({
      query: ({ kolId }) => ({
        url: getWorkspaceApiUrl(`/irm-cooperation-note/${kolId}`),
      }),
      extraOptions: {
        dataSchema: fetchNoteResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.DirectoryNote],
    }),
    deleteNote: builder.mutation<void, DeleteNoteRequest>({
      query: ({ id }) => ({
        url: getWorkspaceApiUrl(`/irm-cooperation-note/${id}`),
        method: 'DELETE',
      }),
      invalidatesTags: [WorkspacesAPITagType.DirectoryNote],
    }),
    addNote: builder.mutation<Note, AddNoteRequest>({
      query: ({ kolId, note }) => ({
        url: getWorkspaceApiUrl(`/irm-cooperation-note/${kolId}`),
        method: 'POST',
        body: note,
      }),
      invalidatesTags: [WorkspacesAPITagType.DirectoryNote],
    }),
    editNote: builder.mutation<void, Note>({
      query: ({ ID, ...body }) => ({
        url: getWorkspaceApiUrl(`/irm-cooperation-note/${ID}`),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [WorkspacesAPITagType.DirectoryNote],
    }),
    fetchDirectorySearchFields: builder.query<
      DirectorySearchFieldsResponse,
      void
    >({
      query: () => getWorkspaceApiUrl('/directory/search-fields', 'v3'),
      extraOptions: {
        dataSchema: directorySearchFieldsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.DirectorySearchFields],
    }),
    createDirectoryCustomizedField: builder.mutation<
      DirectoryCustomizedSearchLeafField,
      string
    >({
      query: (type) => {
        return {
          url: getWorkspaceApiUrl('/directory/customized-fields', 'v3'),
          method: 'POST',
          body: { type },
        }
      },
      extraOptions: {
        dataSchema: directoryCustomizedSearchLeafFieldSchema,
      },
    }),
    changeDirectoryCustomizedField: builder.mutation<
      void,
      Pick<DirectoryCustomizedSearchLeafField, 'id' | 'type'>
    >({
      query: ({ id, type }) => {
        return {
          url: getWorkspaceApiUrl(`/directory/customized-fields/${id}`, 'v3'),
          method: 'PATCH',
          body: { type },
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.DirectorySearchFields,
        WorkspacesAPITagType.KolManagement,
      ],
    }),
    deleteDirectoryCustomizedField: builder.mutation<void, number>({
      query: (customizedFieldId) => {
        return {
          url: getWorkspaceApiUrl(
            `/directory/customized-fields/${customizedFieldId}`,
            'v3',
          ),
          method: 'DELETE',
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.DirectorySearchFields,
        WorkspacesAPITagType.KolManagement,
      ],
    }),
    updateDirectorySearchFields: builder.mutation<void, DirectorySearchFields>({
      query: (body) => {
        return {
          url: getWorkspaceApiUrl(`/directory/search-fields`, 'v3'),
          method: 'PUT',
          body,
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.DirectorySearchFields,
        WorkspacesAPITagType.KolManagement,
      ],
    }),
    fetchKolCollectionSearchFields: builder.query<
      DirectorySearchFieldsResponse,
      FetchKolCollectionSearchFieldsRequest
    >({
      query: ({ id }) =>
        getWorkspaceApiUrl(
          `/directory/kol-collections/${id}/search-fields`,
          'v3',
        ),
      extraOptions: {
        dataSchema: directorySearchFieldsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.KolCollectionSearchFields],
    }),
    updateKolCollectionSearchFields: builder.mutation<
      void,
      KolCollectionBasicRequest & DirectorySearchFields
    >({
      query: ({ collectionId, ...body }) => {
        return {
          url: getWorkspaceApiUrl(
            `/directory/kol-collections/${collectionId}/search-fields`,
            'v3',
          ),
          method: 'PUT',
          body,
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.KolCollectionSearchFields,
      ],
    }),
    fetchKolContactInfos: builder.query<
      FetchKolContactInfoResponse,
      FetchKolContactInfoRequest
    >({
      query: ({ kolId }) => ({
        url: getWorkspaceApiUrl(`/kols/${kolId}/directory/contact-infos`, 'v3'),
      }),
      extraOptions: {
        dataSchema: fetchKolContactInfoResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.DirectoryKolContactInfo],
    }),
    addKolContactInfo: builder.mutation<void, AddKolContactInfoRequest>({
      query: ({ kolId, contactInfo }) => ({
        url: getWorkspaceApiUrl(`/kols/${kolId}/directory/contact-info`, 'v3'),
        method: 'POST',
        body: contactInfo,
      }),
      invalidatesTags: [
        WorkspacesAPITagType.DirectoryKolContactInfo,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
      ],
    }),
    editKolContactInfo: builder.mutation<void, EditKolContactInfoRequest>({
      query: ({ kolId, contactInfoId, contactInfo }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/directory/contact-infos/${contactInfoId}`,
          'v3',
        ),
        method: 'PATCH',
        body: contactInfo,
      }),
      invalidatesTags: [
        WorkspacesAPITagType.DirectoryKolContactInfo,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
      ],
    }),
    deleteKolContactInfo: builder.mutation<void, DeleteKolContactInfoRequest>({
      query: ({ kolId, contactInfoId }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/directory/contact-infos/${contactInfoId}`,
          'v3',
        ),
        method: 'DELETE',
      }),
      invalidatesTags: [
        WorkspacesAPITagType.DirectoryKolContactInfo,
        WorkspacesAPITagType.KolManagement,
      ],
    }),
    fetchInfiniteFilterResources: builder.infiniteQuery<
      FetchFilterResourcesInfiniteResponse,
      Omit<FetchFilterResourcesRequest, 'page'>,
      FetchFilterResourcesRequest['page']
    >({
      query({ queryArg, pageParam }) {
        const params = { ...queryArg, page: pageParam }
        return {
          url: getWorkspaceApiUrl(`/filter-resources`, 'v3'),
          params: removeNil(params),
        }
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.total_page <= lastPageParam) {
            return null
          }

          return lastPageParam + 1
        },
      },
      extraOptions: {
        dataSchema: fetchFilterResourcesInfiniteSchema,
      },
      providesTags: (_result, _error, arg) => [
        {
          type: WorkspacesAPITagType.FilterResources,
          id: arg.filter_resource_type,
        },
      ],
    }),
    fetchDirectoryKolBasicInfo: builder.query<
      FetchKolBasicInfoResponse,
      FetchKolBasicInfoRequest
    >({
      query: ({ kolId }) => ({
        url: getWorkspaceApiUrl(`/directory/kols/${kolId}/basic-info`, 'v3'),
      }),
      extraOptions: {
        dataSchema: fetchKolBasicInfoResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.DirectoryKolBasicInfo],
    }),
    editDirectoryKolBasicInfo: builder.mutation<void, EditKolBasicInfoRequest>({
      query: ({ kolId, basicInfo }) => ({
        url: getWorkspaceApiUrl(`/directory/kols/${kolId}/basic-info`, 'v3'),
        method: 'PUT',
        body: basicInfo,
      }),
      invalidatesTags: [
        WorkspacesAPITagType.DirectoryKolBasicInfo,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
      ],
    }),
    deleteDirectoryKolBasicInfo: builder.mutation<
      void,
      DeleteKolBasicInfoRequest
    >({
      query: ({ kolId, deleteField }) => ({
        url: getWorkspaceApiUrl(`/directory/kols/${kolId}/basic-info`, 'v3'),
        method: 'DELETE',
        body: { delete_field: deleteField },
      }),
      invalidatesTags: [
        WorkspacesAPITagType.DirectoryKolBasicInfo,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
      ],
    }),
    fetchCustomizedLabels: builder.query<FetchCustomizedLabelsResponse, void>({
      query: () => getWorkspaceApiUrl(`/customized-labels`, 'v3'),
      extraOptions: {
        dataSchema: fetchCustomizedLabelsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.CustomizedLabels],
    }),
    createCustomizedLabel: builder.mutation<void, string>({
      query: (customized_label) => {
        return {
          url: getWorkspaceApiUrl('/customized-label', 'v3'),
          method: 'POST',
          body: { customized_label },
        }
      },
      invalidatesTags: [WorkspacesAPITagType.CustomizedLabels],
    }),
    fetchCustomizedFields: builder.query<
      FetchCustomizedFieldsResponse,
      FetchCustomizedFieldsRequest
    >({
      query: ({ kolId, field_type }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/directory/customized-fields`,
          'v3',
        ),
        params: { field_type },
      }),
      extraOptions: {
        dataSchema: fetchCustomizedFieldsSchema,
      },
      providesTags: [WorkspacesAPITagType.DirectoryKolCustomizedFields],
    }),
    updateCustomizedFieldValue: builder.mutation<
      void,
      UpdateCustomizedFieldValueRequest
    >({
      query: ({ kolId, payload }) => ({
        url: getWorkspaceApiUrl(
          `/directory/kol-management/${kolId}/customized-field`,
          'v3',
        ),
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [
        WorkspacesAPITagType.DirectoryKolCustomizedFields,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
      ],
    }),
    fetchInfiniteKolFootprint: builder.query<
      FetchKolFootprintResponse,
      FetchKolFootprintRequest
    >({
      query: ({ kolId, footprint_resource_type, next_page }) => ({
        url: getWorkspaceApiUrl(`/kols/${kolId}/directory/footprints`, 'v3'),
        params: removeNil({ footprint_resource_type, next_page }),
      }),
      extraOptions: {
        dataSchema: fetchKolFootprintResponseSchema,
      },
      // todo : 統一 infinite config setting https://www.notion.so/ikala/infinity-config-setting-138fb0834d6e80b1a1c7e80dc41b1580?pvs=4
      serializeQueryArgs: ({
        endpointName,
        queryArgs,
        endpointDefinition,
      }): string => {
        return defaultSerializeQueryArgs({
          endpointName,
          queryArgs: omit(queryArgs, ['next_page']),
          endpointDefinition,
        })
      },
      merge: (currentCache, newResponse, { arg }): void => {
        const updateCache = {
          data: {
            ...newResponse.data,
            footprints: !isEmpty(arg.next_page)
              ? [
                  ...currentCache.data.footprints,
                  ...newResponse.data.footprints,
                ]
              : newResponse.data.footprints,
          },
        }
        Object.assign(currentCache, updateCache)
      },
      forceRefetch({ currentArg, previousArg }): boolean {
        return !isEqual(currentArg, previousArg)
      },
      providesTags: [
        {
          type: WorkspacesAPITagType.DirectoryKolFootprint,
        },
      ],
    }),
    fetchDirectoryKolCollaborationRecords: builder.query<
      FetchKolManagementCollaborationRecordsResponse,
      FetchKolManagementCollaborationRecordsRequest
    >({
      query: ({ kolId, ...params }) => ({
        url: getWorkspaceApiUrl(
          `/directory/kol-management/${kolId}/collaboration-records`,
          'v3',
        ),
        params,
      }),
      extraOptions: {
        dataSchema: fetchKolManagementCollaborationRecordsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.DirectoryKolCollectionInfo],
    }),
    createKolCollectionCustomizedField: builder.mutation<
      DirectoryCustomizedSearchLeafField,
      CreateKolCollectionCustomizedFieldRequest
    >({
      query: ({ collectionId, type }) => {
        return {
          url: getWorkspaceApiUrl(
            `/directory/kol-collections/${collectionId}/customized-fields`,
            'v3',
          ),
          method: 'POST',
          body: { type },
        }
      },
      extraOptions: {
        dataSchema: directoryCustomizedSearchLeafFieldSchema,
      },
      invalidatesTags: [WorkspacesAPITagType.CollectionSettings],
    }),
    deleteKolCollectionCustomizedField: builder.mutation<
      void,
      DeleteKolCollectionCustomizedFieldRequest
    >({
      query: ({ collectionId, id }) => {
        return {
          url: getWorkspaceApiUrl(
            `/directory/kol-collections/${collectionId}/customized-fields/${id}`,
            'v3',
          ),
          method: 'DELETE',
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.KolCollectionSearchFields,
        WorkspacesAPITagType.CollectionSettings,
      ],
    }),
    changeKolCollectionCustomizedField: builder.mutation<
      void,
      ChangeKolCollectionCustomizedFieldRequest
    >({
      query: ({ collectionId, id, type }) => {
        return {
          url: getWorkspaceApiUrl(
            `/directory/kol-collections/${collectionId}/customized-fields/${id}`,
            'v3',
          ),
          method: 'PATCH',
          body: { type },
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.KolCollectionSearchFields,
        WorkspacesAPITagType.CollectionSettings,
      ],
    }),
    fetchKolManagementStatus: builder.query<KolManagementStatus, void>({
      query: () => getWorkspaceApiUrl('/directory/kol-management/status', 'v3'),
      extraOptions: {
        dataSchema: kolManagementStatusSchema,
      },
      providesTags: [WorkspacesAPITagType.KolManagementStatus],
    }),
    fetchReportCollaborationRecordsInfinite: builder.infiniteQuery<
      FetchCustomPostCollaborationRecordsResponse,
      string,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.data.total_page <= lastPageParam) {
            return null
          }

          return lastPageParam + 1
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${queryArg}/directory/report-collaboration-records`,
          'v3',
        ),
        params: { page: pageParam },
      }),
      extraOptions: {
        dataSchema: fetchCustomPostCollaborationRecordsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.ReportCollaborationRecords],
    }),
    fetchCampaignCollaborationRecordsInfinite: builder.infiniteQuery<
      FetchCollaborationRecordsResponse,
      string,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.data.total_page <= lastPageParam) {
            return null
          }

          return lastPageParam + 1
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${queryArg}/directory/campaign-collaboration-records`,
          'v3',
        ),
        params: { page: pageParam },
      }),
      extraOptions: {
        dataSchema: fetchCollaborationRecordsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.CampaignCollaborationRecords],
    }),
    fetchCustomizedCollaborationRecordsInfinite: builder.infiniteQuery<
      FetchCollaborationRecordsResponse,
      string,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.data.total_page <= lastPageParam) {
            return null
          }

          return lastPageParam + 1
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${queryArg}/directory/customized-collaboration-records`,
          'v3',
        ),
        params: { page: pageParam },
      }),
      extraOptions: {
        dataSchema: fetchCollaborationRecordsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.CustomizedCollaborationRecords],
    }),
    addCustomizedCollaborationRecord: builder.mutation<
      AddCustomizedCollaborationRecordResponse,
      AddCustomizedCollaborationRecordRequest
    >({
      query: ({ kolId, record }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/directory/customized-collaboration-record`,
          'v3',
        ),
        method: 'POST',
        body: { ...record },
      }),
      extraOptions: {
        dataSchema: addCustomizedCollaborationRecordResponseSchema,
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.DirectoryKolFootprint,
        WorkspacesAPITagType.CustomizedCollaborationRecords,
      ],
    }),
    editCustomizedCollaborationRecord: builder.mutation<
      void,
      UpdateCustomizedCollaborationRecordRequest
    >({
      query: ({ kolId, id, record }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/directory/customized-collaboration-records/${id}`,
          'v3',
        ),
        method: 'PUT',
        body: { ...record },
      }),
      invalidatesTags: [
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.DirectoryKolFootprint,
        WorkspacesAPITagType.CustomizedCollaborationRecords,
      ],
    }),
    deleteCustomizedCollaborationRecord: builder.mutation<
      void,
      DeleteCustomizedCollaborationRecordRequest
    >({
      query: ({ kolId, id }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/directory/customized-collaboration-records/${id}`,
          'v3',
        ),
        method: 'DELETE',
      }),
      invalidatesTags: [
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.DirectoryKolFootprint,
        WorkspacesAPITagType.CustomizedCollaborationRecords,
      ],
    }),
  }),
  overrideExisting: false,
})

export const invalidateDirectoryKolFootprintAction =
  directoryApi.util.invalidateTags([WorkspacesAPITagType.DirectoryKolFootprint])

export const {
  useAddKolsToDirectoryMutation,
  useRemoveKolsFromDirectoryMutation,
  useFetchNoteQuery,
  useDeleteNoteMutation,
  useAddNoteMutation,
  useEditNoteMutation,
  useFetchDirectorySearchFieldsQuery,
  useCreateDirectoryCustomizedFieldMutation,
  useChangeDirectoryCustomizedFieldMutation,
  useDeleteDirectoryCustomizedFieldMutation,
  useUpdateDirectorySearchFieldsMutation,
  useFetchKolCollectionSearchFieldsQuery,
  useUpdateKolCollectionSearchFieldsMutation,
  useFetchKolContactInfosQuery,
  useAddKolContactInfoMutation,
  useEditKolContactInfoMutation,
  useDeleteKolContactInfoMutation,
  useFetchDirectoryKolBasicInfoQuery,
  useFetchCustomizedLabelsQuery,
  useCreateCustomizedLabelMutation,
  useFetchCustomizedFieldsQuery,
  useUpdateCustomizedFieldValueMutation,
  useFetchInfiniteKolFootprintQuery,
  useFetchDirectoryKolCollaborationRecordsQuery,
  useCreateKolCollectionCustomizedFieldMutation,
  useDeleteKolCollectionCustomizedFieldMutation,
  useChangeKolCollectionCustomizedFieldMutation,
  useFetchKolManagementStatusQuery,
} = directoryApi

//SECTION - Directory Kol Management
export const useFetchKolManagementQuery =
  directoryApi.endpoints.fetchKolManagement.useQuery

//SECTION - Directory Kol Basic Info
export const useEditDirectoryKolBasicInfoMutation =
  directoryApi.endpoints.editDirectoryKolBasicInfo.useMutation
export const useDeleteDirectoryKolBasicInfoMutation =
  directoryApi.endpoints.deleteDirectoryKolBasicInfo.useMutation

//SECTION - Directory Collaboration Record
export const useFetchReportCollaborationRecordsInfiniteQuery =
  directoryApi.endpoints.fetchReportCollaborationRecordsInfinite
    .useInfiniteQuery
export const useFetchCampaignCollaborationRecordsInfiniteQuery =
  directoryApi.endpoints.fetchCampaignCollaborationRecordsInfinite
    .useInfiniteQuery
export const useFetchCustomizedCollaborationRecordsInfiniteQuery =
  directoryApi.endpoints.fetchCustomizedCollaborationRecordsInfinite
    .useInfiniteQuery
export const useFetchInfiniteFilterResourcesInfiniteQuery =
  directoryApi.endpoints.fetchInfiniteFilterResources.useInfiniteQuery
export const useAddCustomizedCollaborationRecordMutation =
  directoryApi.endpoints.addCustomizedCollaborationRecord.useMutation
export const useEditCustomizedCollaborationRecordMutation =
  directoryApi.endpoints.editCustomizedCollaborationRecord.useMutation
export const useDeleteCustomizedCollaborationRecordMutation =
  directoryApi.endpoints.deleteCustomizedCollaborationRecord.useMutation
export const useAddCollaborationRecordScoringMutation =
  directoryApi.endpoints.addCollaborationRecordScoring.useMutation
export const useEditCollaborationRecordScoringMutation =
  directoryApi.endpoints.editCollaborationRecordScoring.useMutation
export const useDeleteCollaborationRecordScoringMutation =
  directoryApi.endpoints.deleteCollaborationRecordScoring.useMutation
